import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type RefObject } from "react";
import type { Prospect, SessionGrant } from "../api/types";
import { Field } from "../components/Field";
import { toApiError } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { useDemoRoom, type BookingNotice, type CallPhase } from "./useDemoRoom";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_BUSY_RETRIES = 4;

export interface DemoFlowProps {
  heading: string;
  intro: string;
  initialProspect?: Partial<Prospect>;
  embedded?: boolean;
  onStart: (prospect: Prospect) => Promise<SessionGrant>;
  onClose?: () => void;
}

type Stage =
  | { kind: "form" }
  | { kind: "starting" }
  | { kind: "waiting"; secondsLeft: number; attempt: number }
  | { kind: "call"; tenantName: string };

interface FormErrors {
  name?: string | undefined;
  email?: string | undefined;
  consent?: string | undefined;
}

const VOICE_ONLY_MESSAGE = "You're talking with the guide. The product appears here when it shares its screen.";

const PHASE_MESSAGES: Record<CallPhase, string> = {
  idle: "Getting ready…",
  connecting: "Connecting you to the demo…",
  waiting_for_guide: "Your guide is opening the product. Say hello when you hear them.",
  live: "Your guide is live.",
  ended: "Call ended.",
  failed: "We lost the connection.",
};

export function DemoFlow({ heading, intro, initialProspect, embedded = false, onStart, onClose }: DemoFlowProps) {
  const {
    phase,
    muted,
    guideSpeaking,
    screenSharing,
    booking,
    error,
    screenRef,
    audioRef,
    connect,
    toggleMute,
    leave,
    reset,
  } = useDemoRoom();
  const consentId = useId();
  const [name, setName] = useState(initialProspect?.name ?? "");
  const [email, setEmail] = useState(initialProspect?.email ?? "");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const prospectRef = useRef<Prospect | null>(null);

  const begin = useCallback(
    async (attempt: number) => {
      const prospect = prospectRef.current;
      if (!prospect) {
        return;
      }
      setStage({ kind: "starting" });
      setFailure(null);
      let grant: SessionGrant;
      try {
        grant = await onStart(prospect);
      } catch (caught) {
        const apiError = toApiError(caught);
        if (apiError.status === 503 && attempt < MAX_BUSY_RETRIES) {
          setStage({ kind: "waiting", secondsLeft: apiError.retryAfterSeconds ?? 15, attempt: attempt + 1 });
          return;
        }
        if (apiError.status === 422) {
          setErrors({
            name: apiError.details.find((detail) => detail.field === "prospect.name")?.message,
            email: apiError.details.find((detail) => detail.field === "prospect.email")?.message,
          });
        }
        setFailure(apiError.message);
        setStage({ kind: "form" });
        return;
      }
      setStage({ kind: "call", tenantName: grant.tenant_name });
      await connect(grant);
    },
    [onStart, connect],
  );

  useEffect(() => {
    if (stage.kind !== "waiting") {
      return;
    }
    const timer = window.setTimeout(() => {
      if (stage.secondsLeft <= 1) {
        void begin(stage.attempt);
      } else {
        setStage({ ...stage, secondsLeft: stage.secondsLeft - 1 });
      }
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [stage, begin]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const next: FormErrors = {};
    if (!trimmedName) {
      next.name = "Tell us your name so the guide knows who it's talking to.";
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      next.email = "Enter your work email. We'll send any booking confirmation there.";
    }
    if (!consent) {
      next.consent = "Please confirm you're happy to talk with an AI guide.";
    }
    setErrors(next);
    if (next.name || next.email || next.consent) {
      return;
    }
    prospectRef.current = { name: trimmedName, email: trimmedEmail };
    void begin(0);
  }

  function restart() {
    reset();
    setStage({ kind: "form" });
  }

  if (stage.kind === "call") {
    return (
      <CallView
        phase={phase}
        muted={muted}
        guideSpeaking={guideSpeaking}
        screenSharing={screenSharing}
        booking={booking}
        error={error}
        screenRef={screenRef}
        audioRef={audioRef}
        tenantName={stage.tenantName}
        embedded={embedded}
        onToggleMute={() => void toggleMute()}
        onLeave={() => void leave()}
        onClose={onClose}
        onRestart={restart}
      />
    );
  }

  const busy = stage.kind !== "form";
  return (
    <section className="demo-card" aria-labelledby="demo-heading">
      <p className="eyebrow">Live demo</p>
      <h1 id="demo-heading" className="demo-title">
        {heading}
      </h1>
      <p className="demo-intro">{intro}</p>
      {failure && (
        <p className="alert alert-error" role="alert">
          {failure}
        </p>
      )}
      {stage.kind === "waiting" && (
        <p className="alert alert-info" role="status">
          Every guide is on a call right now. Trying again in {stage.secondsLeft}s…
        </p>
      )}
      <form className="stack" onSubmit={submit} noValidate>
        <Field label="Your name" error={errors.name}>
          {(props) => (
            <input
              {...props}
              name="name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={busy}
            />
          )}
        </Field>
        <Field label="Work email" error={errors.email}>
          {(props) => (
            <input
              {...props}
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={busy}
            />
          )}
        </Field>
        <div className="checkbox-field">
          <input
            id={consentId}
            type="checkbox"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
            aria-invalid={Boolean(errors.consent)}
            aria-describedby={errors.consent ? `${consentId}-error` : undefined}
            disabled={busy}
          />
          <label htmlFor={consentId}>
            I understand I'm talking with an AI guide, and that the team may follow up with me by email.
          </label>
        </div>
        {errors.consent && (
          <p id={`${consentId}-error`} className="field-error">
            {errors.consent}
          </p>
        )}
        <button type="submit" className="btn btn-accent btn-lg" disabled={busy}>
          {stage.kind === "starting" ? "Connecting…" : "Start the call"}
        </button>
        <p className="field-hint">The guide uses your microphone. Headphones help it hear you clearly.</p>
      </form>
      {embedded && onClose && (
        <button type="button" className="btn btn-ghost btn-block" onClick={onClose}>
          Close
        </button>
      )}
    </section>
  );
}

interface CallViewProps {
  phase: CallPhase;
  muted: boolean;
  guideSpeaking: boolean;
  screenSharing: boolean;
  booking: BookingNotice | null;
  error: string | null;
  screenRef: RefObject<HTMLVideoElement | null>;
  audioRef: RefObject<HTMLDivElement | null>;
  tenantName: string;
  embedded: boolean;
  onToggleMute: () => void;
  onLeave: () => void;
  onClose: (() => void) | undefined;
  onRestart: () => void;
}

function CallView({
  phase,
  muted,
  guideSpeaking,
  screenSharing,
  booking,
  error,
  screenRef,
  audioRef,
  tenantName,
  embedded,
  onToggleMute,
  onLeave,
  onClose,
  onRestart,
}: CallViewProps) {
  if (phase === "ended" || phase === "failed") {
    return (
      <section className="demo-card stack" aria-live="polite">
        {phase === "failed" ? (
          <>
            <h1 className="demo-title">We couldn't keep the call going</h1>
            <p className="alert alert-error" role="alert">
              {error ?? "The connection dropped. Please try again."}
            </p>
          </>
        ) : (
          <>
            <h1 className="demo-title">Thanks for taking a look</h1>
            <p>
              {booking
                ? `Your call with the ${tenantName} team is booked for ${formatDateTime(booking.starts_at, booking.timezone)}. The invite is in your inbox.`
                : "Whenever you're ready, start another call and pick up where you left off."}
            </p>
          </>
        )}
        <div className="row">
          <button type="button" className="btn btn-accent" onClick={onRestart}>
            Start a new call
          </button>
          {embedded && onClose && (
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </section>
    );
  }

  const live = phase === "live";
  const showScreen = live && screenSharing;
  return (
    <section className="demo-call" aria-label={`Live demo with ${tenantName}`}>
      <div className="demo-stage">
        <video ref={screenRef} className="demo-video" data-visible={showScreen} autoPlay playsInline muted />
        {!showScreen && (
          <div className="demo-placeholder">
            {live ? (
              <span className={guideSpeaking ? "status-dot is-speaking" : "status-dot"} aria-hidden="true" />
            ) : (
              <span className="spinner" aria-hidden="true" />
            )}
            <p>{live ? VOICE_ONLY_MESSAGE : PHASE_MESSAGES[phase]}</p>
          </div>
        )}
      </div>
      <div ref={audioRef} className="visually-hidden" />
      {booking && (
        <p className="booking-card" role="status">
          <strong>Call booked:</strong> {formatDateTime(booking.starts_at, booking.timezone)}. The invite is on its way
          to your inbox.
        </p>
      )}
      <div className="demo-bar">
        <p className="demo-status" aria-live="polite">
          <span className={guideSpeaking ? "status-dot is-speaking" : "status-dot"} aria-hidden="true" />
          {live ? (guideSpeaking ? "Guide is speaking" : "Listening") : PHASE_MESSAGES[phase]}
        </p>
        <div className="demo-controls">
          <button type="button" className="btn btn-secondary" onClick={onToggleMute} disabled={phase === "connecting"}>
            {muted ? "Unmute" : "Mute"}
          </button>
          <button type="button" className="btn btn-danger" onClick={onLeave}>
            End call
          </button>
        </div>
      </div>
    </section>
  );
}
